import { WishlistStore } from './casonda-wishlist.js';
import { CartLinesUpdateEvent } from '@shopify/events';

const heart = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 21 3.5 13A5.8 5.8 0 0 1 12 5a5.8 5.8 0 0 1 8.5 8Z"/></svg>';
const bag = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M4 7h16l1 15H3L4 7Z"/><path d="M8 9V6a4 4 0 0 1 8 0v3"/></svg>';
const element = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

class CasondaWishlistPage extends HTMLElement {
  products = new Map();
  loading = new Set();
  choices = new Map();
  busy = false;
  generation = 0;

  connectedCallback() {
    this.root = (this.dataset.root || '/').replace(/\/?$/, '/');
    this.money = new Intl.NumberFormat(this.dataset.locale || 'en', {
      style: 'currency', currency: this.dataset.currency || 'USD',
    });
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.status = this.querySelector('[data-wishlist-status]');
    this.all = this.querySelector('[data-add-all]');
    this.all.addEventListener('click', this.addAll);
    document.addEventListener('wishlist:update', this.refresh);
    window.addEventListener('storage', this.onStorage);
    this.refresh();
  }

  disconnectedCallback() {
    this.generation++;
    this.all.removeEventListener('click', this.addAll);
    document.removeEventListener('wishlist:update', this.refresh);
    window.removeEventListener('storage', this.onStorage);
  }

  onStorage = (event) => {
    if (event.key === 'casonda-wishlist' || event.key === null) this.refresh();
  };

  items() {
    const seen = new Set();
    return WishlistStore.getItems().filter((item) => {
      if (!item || !Number.isSafeInteger(item.id) || seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }

  productUrl(item) {
    const url = new URL(item.url, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid product link');
    const match = url.pathname.match(/\/products\/([a-z0-9-]+)\/?$/i);
    if (!match) throw new Error('Invalid product link');
    return `${this.root}products/${match[1]}`;
  }

  refresh = async () => {
    const generation = ++this.generation;
    const items = this.items();
    this.loading = new Set(items.filter((item) => !this.products.has(item.id)).map((item) => item.id));
    this.render();
    if (this.loading.size === 0) {
      this.grid.setAttribute('aria-busy', 'false');
      return;
    }
    this.grid.setAttribute('aria-busy', 'true');
    // Limit parallel requests, including for large existing wishlists.
    for (let start = 0; start < items.length; start += 4) {
      await Promise.all(items.slice(start, start + 4).map(async (item) => {
        if (this.products.has(item.id)) return;
        try {
          const response = await fetch(`${this.productUrl(item)}.js`, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Product unavailable');
          const product = await response.json();
          if (product.id !== item.id || !Array.isArray(product.variants)) throw new Error('Invalid product');
          this.products.set(item.id, product);
        } catch {
          // Preserve the saved item; show a retry action rather than a stale price.
        }
        this.loading.delete(item.id);
      }));
      if (generation !== this.generation || !this.isConnected) return;
    }
    this.render();
    this.grid.setAttribute('aria-busy', 'false');
  };

  selected(item) {
    const product = this.products.get(item.id);
    if (!product) return null;
    if (product.variants.length === 1) return product.variants[0];
    return product.variants.find((variant) => variant.id === this.choices.get(item.id)) || null;
  }

  render() {
    const items = this.items();
    this.querySelector('[data-wishlist-count]').textContent = `${items.length} ${items.length === 1 ? 'item' : 'items'} saved`;
    this.querySelector('[data-wishlist-empty]').hidden = items.length > 0;
    this.all.disabled = this.busy || !items.some((item) => this.selected(item)?.available);
    this.grid.replaceChildren(...items.map((item) => this.card(item)));
  }

  card(item) {
    const product = this.products.get(item.id);
    const card = element('article', 'casonda-wishlist-card');
    const media = element('div', 'casonda-wishlist-card__media');
    let href;
    try { href = this.productUrl(item); } catch { /* Invalid stored URL remains removable. */ }
    const link = element(href ? 'a' : 'div');
    if (href) link.href = href;
    // Never inject saved product strings as HTML or trust stored image URLs.
    const image = product?.featured_image;
    if (image) {
      // Shopify returns protocol-relative CDN URLs; local HTTP previews must
      // still request those images over HTTPS.
      const url = new URL(image.startsWith('//') ? `https:${image}` : image, location.origin);
      if (url.protocol === 'https:' || url.origin === location.origin) {
        const img = element('img');
        url.searchParams.set('width', '600');
        img.src = url.href;
        img.alt = product.title;
        img.loading = 'lazy';
        img.width = 480;
        img.height = 548;
        link.append(img);
      }
    }
    media.append(link);
    const remove = element('button', 'casonda-wishlist-card__remove');
    remove.type = 'button';
    remove.innerHTML = heart;
    remove.setAttribute('aria-label', `Remove ${product?.title || item.title || 'product'} from wishlist`);
    remove.disabled = this.busy;
    remove.addEventListener('click', () => {
      if (WishlistStore.has(item.id)) WishlistStore.toggle(item);
      (this.querySelector('.casonda-wishlist-card__remove') || this.querySelector('[data-wishlist-empty] a'))?.focus();
    });
    media.append(remove);
    card.append(media);
    const heading = element('h2');
    const title = element(href ? 'a' : 'span', '', product?.title || item.title || 'Saved product');
    if (href) title.href = href;
    heading.append(title);
    card.append(heading);
    if (!product) {
      card.append(element('p', 'casonda-wishlist-card__variant', this.loading.has(item.id) ? 'Loading product details…' : 'Product details could not be loaded.'));
      const retry = element('button', 'casonda-wishlist-card__move', 'Retry');
      retry.type = 'button';
      retry.disabled = this.busy || this.loading.has(item.id);
      retry.addEventListener('click', this.refresh);
      card.append(retry);
      return card;
    }
    const variant = this.selected(item);
    if (product.variants.length > 1) {
      const label = element('label', '', 'Choose an option');
      label.htmlFor = `wishlist-variant-${item.id}`;
      const select = element('select');
      select.id = label.htmlFor;
      select.disabled = this.busy;
      const placeholder = element('option', '', 'Select an option');
      placeholder.value = '';
      select.append(placeholder);
      product.variants.forEach((option) => {
        const node = element('option', '', `${option.title}${option.available ? '' : ' — Sold out'}`);
        node.value = String(option.id);
        node.disabled = !option.available;
        select.append(node);
      });
      select.value = variant ? String(variant.id) : '';
      select.addEventListener('change', () => {
        this.choices.set(item.id, Number(select.value));
        this.render();
        this.querySelector(`#wishlist-variant-${item.id}`)?.focus();
      });
      card.append(label, select);
    } else if (variant?.title !== 'Default Title') {
      card.append(element('p', 'casonda-wishlist-card__variant', variant?.title || ''));
    }
    const bottom = element('div', 'casonda-wishlist-card__bottom');
    bottom.append(element('span', '', `${!variant && product.price_varies ? 'From ' : ''}${this.money.format((variant?.price ?? product.price) / 100)}`));
    const move = element('button', 'casonda-wishlist-card__move');
    move.type = 'button';
    move.innerHTML = bag;
    move.append(document.createTextNode(!product.available ? 'Sold out' : !variant ? 'Choose an option' : 'Move to cart'));
    move.disabled = this.busy || !variant?.available;
    move.addEventListener('click', () => this.move([item]));
    bottom.append(move);
    card.append(bottom);
    return card;
  }

  addAll = () => this.move(this.items());

  async move(items) {
    if (this.busy) return;
    this.busy = true;
    const eligible = items.map((item) => ({ item, variant: this.selected(item) })).filter(({ variant }) => variant?.available);
    this.render();
    this.status.textContent = 'Adding to cart…';
    let added = 0;
    const successes = [];
    let sections;
    const failures = [];
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((node) => node.dataset.sectionId))].slice(0, 5);
    for (const { item, variant } of eligible) {
      try {
        const response = await fetch(`${this.root}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }], sections: sectionIds, sections_url: location.pathname }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'Could not add this item.');
        added++;
        successes.push(variant);
        sections = result.sections;
        if (WishlistStore.has(item.id)) WishlistStore.toggle(item);
      } catch (error) {
        failures.push(`${this.products.get(item.id)?.title || 'Product'}: ${error.message}`);
      }
    }
    if (added) {
      try {
        const response = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Cart refresh failed');
        const cart = await response.json();
        this.dispatchEvent(new CartLinesUpdateEvent({
          action: 'add', context: 'product',
          lines: successes.map((variant) => ({ merchandiseId: String(variant.id), quantity: 1 })),
          promise: Promise.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: {
            source: 'wishlist-page', sections, items: cart.items, itemCount: added,
          } }),
        }));
      } catch {
        failures.push('Items were added, but the cart display could not refresh. Open the cart to see them.');
      }
    }
    this.busy = false;
    this.render();
    const skipped = items.length - eligible.length;
    this.status.textContent = [added ? `${added} ${added === 1 ? 'item moved' : 'items moved'} to cart.` : 'No items were moved.',
      skipped ? `${skipped} ${skipped === 1 ? 'item needs' : 'items need'} an option selection or is unavailable.` : '', ...failures].filter(Boolean).join(' ');
  }
}

if (!customElements.get('casonda-wishlist-page')) customElements.define('casonda-wishlist-page', CasondaWishlistPage);
